import { HighlightType } from '@node-space/ui-components'
import { EarnAssetTransaction } from '../types'

export const EARN_TRANSACTION_CATEGORY = {
  DEPOSIT: 'DEPOSIT',
  REWARD_PAYOUT: 'REWARD_PAYOUT',
  REWARD_WITHDRAWAL: 'REWARD_WITHDRAWAL',
} as const

export type EarnTransactionCategory =
  (typeof EARN_TRANSACTION_CATEGORY)[keyof typeof EARN_TRANSACTION_CATEGORY]

// Real ledger `type` values observed for rewards/earn wallets (see rewardsTransactions mock/handler)
const LEDGER_TYPE_TO_EARN_CATEGORY: Record<string, EarnTransactionCategory> = {
  rewardsDeposit: EARN_TRANSACTION_CATEGORY.DEPOSIT,
  rewardsYield: EARN_TRANSACTION_CATEGORY.REWARD_PAYOUT,
  rewardsEarned: EARN_TRANSACTION_CATEGORY.REWARD_PAYOUT,
  rewardsWithdrawal: EARN_TRANSACTION_CATEGORY.REWARD_WITHDRAWAL,
}

export const EARN_TRANSACTION_TYPE_LABEL_KEYS: Record<EarnTransactionCategory, string> = {
  [EARN_TRANSACTION_CATEGORY.DEPOSIT]: 'earn.transactionType.deposit',
  [EARN_TRANSACTION_CATEGORY.REWARD_PAYOUT]: 'earn.transactionType.rewardPayout',
  [EARN_TRANSACTION_CATEGORY.REWARD_WITHDRAWAL]: 'earn.transactionType.rewardWithdrawal',
}

export const getEarnTransactionCategory = (
  transaction: EarnAssetTransaction
): EarnTransactionCategory | undefined => LEDGER_TYPE_TO_EARN_CATEGORY[transaction.type]

export const EARN_TRANSACTION_STATUS = {
  PENDING: 'PENDING',
  COMPLETE: 'COMPLETE',
  FAILED: 'FAILED',
} as const

export type EarnTransactionStatus =
  (typeof EARN_TRANSACTION_STATUS)[keyof typeof EARN_TRANSACTION_STATUS]

// Real source status values observed for rewards/earn wallet transactions
const FAILED_SOURCE_STATUSES = new Set(['FAILED', 'CANCELLED', 'EXPIRED', 'RETURNED'])

export const getEarnTransactionStatus = (
  transaction: EarnAssetTransaction
): EarnTransactionStatus => {
  if (transaction.status === 'COMPLETED') {
    return EARN_TRANSACTION_STATUS.COMPLETE
  }
  if (FAILED_SOURCE_STATUSES.has(transaction.status)) {
    return EARN_TRANSACTION_STATUS.FAILED
  }
  return EARN_TRANSACTION_STATUS.PENDING
}

export const EARN_TRANSACTION_STATUS_LABEL_KEYS: Record<EarnTransactionStatus, string> = {
  [EARN_TRANSACTION_STATUS.PENDING]: 'earn.transactionStatus.pending',
  [EARN_TRANSACTION_STATUS.COMPLETE]: 'earn.transactionStatus.complete',
  [EARN_TRANSACTION_STATUS.FAILED]: 'earn.transactionStatus.failed',
}

const EARN_TRANSACTION_STATUS_HIGHLIGHT_TYPES: Record<EarnTransactionStatus, HighlightType> = {
  [EARN_TRANSACTION_STATUS.PENDING]: HighlightType.Neutral,
  [EARN_TRANSACTION_STATUS.COMPLETE]: HighlightType.Success,
  [EARN_TRANSACTION_STATUS.FAILED]: HighlightType.Error,
}

export const getEarnTransactionStatusHighlightType = (
  transaction: EarnAssetTransaction
): HighlightType => EARN_TRANSACTION_STATUS_HIGHLIGHT_TYPES[getEarnTransactionStatus(transaction)]
